import React, { useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { toast } from "react-hot-toast";
import apiClient from "../../services/apiClient";
import { useSelector } from "react-redux";

const ReturnFormPopup = ({ order, onClose }) => {
  const [returnReason, setReturnReason] = useState("");
  const [otherReasonText, setOtherReasonText] = useState("");
  const [returnImages, setReturnImages] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const isWithinReturnPeriod = (deliveredDate) => {
    if (!deliveredDate) return false;
    const delivered = new Date(deliveredDate);
    const today = new Date();
    const diffTime = today - delivered;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= 5; // 5-day return window
  };

  const handleReturnSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedProductId) {
      toast.error("Please select a product to return");
      return;
    }

    if (!returnReason) {
      toast.error("Please select a return reason");
      return;
    }

    if (returnReason === 'Other' && !otherReasonText.trim()) {
      toast.error("Please provide details for your return reason");
      return;
    }

    if (returnImages.length === 0) {
      toast.error("Please provide at least one image proof");
      return;
    }

    const returnItem = order.order_items.find(item => item.id == selectedProductId);
    if (!returnItem) {
      toast.error("Selected product not found");
      return;
    }

    // Check if within return period
    if (!isWithinReturnPeriod(order.delivered_date)) {
      toast.error("Return period has expired (5 days from delivery)");
      return;
    }

    const formData = new FormData();
    const returnAmount = parseFloat(returnItem.price) * returnItem.quantity;
    
    formData.append('order_id', order.id);
    formData.append('order_item_id', selectedProductId);
    formData.append('user_id', user.id);
    formData.append('returned_amount', returnAmount);
    formData.append('reason', returnReason === 'Other' ? otherReasonText : returnReason);
    
    returnImages.forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });

    setSubmitting(true);
    
    apiClient.post('/returns', formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then(() => {
      toast.success("Return request submitted successfully!");
      onClose();
    })
    .catch((err) => {
      console.error(err);
      toast.error("Failed to submit return request");
    })
    .finally(() => {
      setSubmitting(false);
    });
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    // Validate file types and sizes
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024 // 5MB limit
    );
    
    if (validFiles.length !== files.length) {
      toast.error("Some files were not valid images or were too large (max 5MB)");
    }
    
    setReturnImages(prev => [...prev, ...validFiles]);
  };

  const handleRemoveImage = (index) => {
    setReturnImages(prev => prev.filter((_, i) => i !== index));
  };

  const reasons = [
    "Wrong item received",
    "Damaged product",
    "Product not as described",
    "Changed my mind",
    "Other",
  ];

  return (
    <div className="ReviewPopupOverlay">
      <div className="ReviewPopupBox">
        <button className="ClosePopupBtn" onClick={onClose}>
          <RxCross2 />
        </button>

        <h3>Return Product</h3>
        
        {!isWithinReturnPeriod(order.delivered_date) && (
          <div className="ReturnPeriodWarning">
            <p>⚠️ Return period has expired (5 days from delivery)</p>
          </div>
        )}

        <form className="ReviewForm" onSubmit={handleReturnSubmit}>
          {/* Product Selection */}
          <div className="FormField">
            <label>Select Product to Return *</label>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              required
              disabled={!isWithinReturnPeriod(order.delivered_date)}
            >
              <option value="">Select a product</option>
              {order.order_items.map((item) => {
                const productName = item.product_type === 'combo' 
                  ? item.combo?.title 
                  : item.product?.name;
                return (
                  <option key={item.id} value={item.id}>
                    {productName} (Qty: {item.quantity})
                  </option>
                );
              })}
            </select>
          </div>

          {/* Return Reason */}
          <div className="FormField">
            <label>Reason for Return *</label>
            <select
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              required
              disabled={!isWithinReturnPeriod(order.delivered_date)}
            >
              <option value="">Select a reason</option>
              {reasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          {returnReason === 'Other' && (
            <div className="FormField">
              <label>Please specify reason *</label>
              <textarea
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                placeholder="Provide details about your return reason"
                rows="3"
                required
                disabled={!isWithinReturnPeriod(order.delivered_date)}
              />
            </div>
          )}

          {/* Image Upload */}
          <div className="FormField">
            <label>Upload Proof Images *</label>
            <div
              className="AddPhotoBox"
              onClick={() => document.getElementById("returnImages").click()}
              style={{ opacity: !isWithinReturnPeriod(order.delivered_date) ? 0.6 : 1 }}
            >
              <span className="PlusIcon">+</span>
              <p>Add Photos</p>
            </div>
            <input
              id="returnImages"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              style={{ display: "none" }}
              disabled={!isWithinReturnPeriod(order.delivered_date)}
            />

            {returnImages.length > 0 && (
              <div className="ImagePreviewGrid">
                {returnImages.map((img, index) => (
                  <div className="ImagePreview" key={index}>
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`Preview-${index}`}
                    />
                    <button
                      type="button"
                      className="RemoveImage"
                      onClick={() => handleRemoveImage(index)}
                      disabled={!isWithinReturnPeriod(order.delivered_date)}
                    >
                      ✖
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button 
            type="submit" 
            className="SubmitButton"
            disabled={submitting || !isWithinReturnPeriod(order.delivered_date)}
          >
            {submitting ? "Submitting..." : "Submit Return Request"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReturnFormPopup;